import { Tooltip } from '@calendly/ui/components/tooltip';
import classNames from 'classnames';
import { forwardRef, useRef, useState } from 'react';
import { useTrack } from 'hooks/analytics_events';
import { NavigationIconContainer, NavigationLabel, navigationLabelCaptionMinimizedCss } from '../elements';
import { useLeftNavigationState } from '../hooks';
import { useExpandPhaseContext } from '../left_navigation_context';
import { NavigationItem } from '../navigation_item';
const navigationItemWrapperCss = "nunh645";

/** Wrapper span: owns pointer cursor and hover state, forwards clicks to the inner anchor when click is outside the anchor. */
const NavigationItemWrapper = forwardRef((props, ref) => <span ref={ref} {...props} className={classNames(navigationItemWrapperCss, props.className)} />);

/** Renders the nav link (anchor) and label; receives isHovered from wrapper so hover style applies across full item area. */
const NavigationItemBody = forwardRef(({
  config,
  isMinimized,
  isHovered,
  ...props
}, ref) => <NavigationItem ref={ref} data-calendly-label={config.dataCalendlyLabel} to={config.to} activePaths={config.activePaths} activePathPrefixes={config.activePathPrefixes} isHovered={isHovered} {...props}>
    <NavigationIconContainer>
      {config.icon}
    </NavigationIconContainer>
    <NavigationLabel>
      {isMinimized ? <span className={navigationLabelCaptionMinimizedCss}>{config.caption}</span> : <>
            {config.caption}
            {config.postTextIcon}
          </>}
    </NavigationLabel>
    {config.inlineObject}
  </NavigationItem>);

/**
 * Default nav item: wrapper span (cursor, hover state, click forwarding) around the nav link.
 * Clicks on the wrapper are forwarded to the inner anchor; clicks on the anchor are not forwarded to avoid double handling.
 * Spotlight (e.g. NotetakerSpotlight) interactions are in the modal (next/prev); nav clicks just navigate.
 *
 * @param {Object} props
 * @param {Object} props.config - Nav item config (to, caption, icon, etc.)
 * @param {React.ReactNode} [config.spotlight] - Optional spotlight UI (e.g. product tour) as sibling to the link.
 */
export const DefaultNavigationItem = ({
  config,
  ...props
}) => {
  const leftNavigationState = useLeftNavigationState();
  const {
    isExpanding,
    expandFadeInStarted
  } = useExpandPhaseContext();
  const isNavMinimized = leftNavigationState === 'phone-collapsed' || leftNavigationState === 'minimized';
  /* During expand, keep minimized label content (no beta/wrapping) until fade-in so nothing flashes */
  const useMinimizedLabel = isNavMinimized || isExpanding && !expandFadeInStarted;
  const wrapperRef = useRef(null);
  const anchorRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  /**
   * Forwards wrapper clicks to the inner nav link when the click is within the wrapper span's bounding box.
   * If the click is outside the span's boundary box we do nothing. If inside, we forward unless
   * the target is already the anchor (avoids double navigation).
   * @param {React.MouseEvent} e - Click event on the wrapper span.
   */
  const handleSpanClick = e => {
    const wrapper = wrapperRef.current;
    const anchor = anchorRef.current;
    if (!wrapper || !anchor) return;
    const rect = wrapper.getBoundingClientRect();
    const {
      clientX,
      clientY
    } = e;
    const inBounds = clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
    if (!inBounds) return;
    if (anchor.contains(e.target)) return;
    anchor.click();
  };
  return <Tooltip text={useMinimizedLabel ? config.caption : null} placement="right">
      <NavigationItemWrapper ref={wrapperRef} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} onClick={handleSpanClick}>
        <NavigationItemBody ref={anchorRef} config={config} isMinimized={useMinimizedLabel} isHovered={isHovered} {...props} />
        {config.spotlight}
      </NavigationItemWrapper>
    </Tooltip>;
};

/**
 * Same layout as DefaultNavigationItem; when `config.analytics` is set, fires `useTrack` on link click
 * (including clicks forwarded from the wrapper span). Merges `source_url` at click time.
 *
 * @param {Object} props.config - Nav config; optional `analytics: { name, properties }` (event name + payload).
 */
export const TrackedNavigationItem = ({
  config,
  ...props
}) => {
  const track = useTrack();
  const {
    analytics,
    ...navConfig
  } = config;
  const {
    onClick: onClickProp,
    ...restProps
  } = props;
  const handleLinkClick = e => {
    if (analytics) {
      track({
        name: analytics.name,
        properties: {
          ...analytics.properties,
          source_url: window.location.href
        }
      });
    }
    onClickProp?.(e);
  };
  return <DefaultNavigationItem config={navConfig} onClick={handleLinkClick} {...restProps} />;
};

require("./default-navigation-item.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./default-navigation-item.js");